import { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    SttChipPurseMov,
    SttChipUser,
    SttButtonNew,
} from '../../../sporttia/all';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import ChargePurseDialog from '../../../components/dialogs/ChargePurseDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { SttFormSearch } from '../../../sporttia/forms/SttFormSearch';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type PaymentData = {
    id: number;
    paymentForm: string;
};

type MovementData = {
    id: number;
    created: string;
    creator: unknown;
    type: string;
    concept: string;
    payment: PaymentData;
    amount: number;
    balance: number;
};

type MovementsResponse = {
    rows?: MovementData[];
    total?: number;
};

type PaginationParams = {
    page: number;
    numRows: number;
    concept?: string;
};

export default function MshipPurseMovs({ mship }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [movs, setMovs] = useState<MovementsResponse>({});
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [chargingPurseMship, setChargingPurseMship] =
        useState<MshipData | null>(null);
    const [searchingkeyword, setSearchingKeyword] = useState<string>('');
    const defaultNumRows = 20;

    useEffect(() => {
        loadMovs();
    }, [mship]);

    const loadMovs = (
        p: PaginationParams = { page: 1, numRows: defaultNumRows, concept: '' },
    ) => {
        cxt?.api('GET', `/mships/${mship.id}/purses/movs`, {
            params: {
                page: p.page,
                rows: p.numRows,
                concept: searchingkeyword,
            },
            success: (r: any) => {
                setMovs(r);
            },
        });
    };

    const handleSearch = ({ searchText }: { searchText?: string }) => {
        loadMovs({
            page: 1,
            numRows: defaultNumRows,
            concept: searchText || '',
        });
    };

    const openPayment = (pay: PaymentData) => {
        setIdPayModal(pay.id);
    };

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Box sx={{ width: 300, maxWidth: '100%' }}>
                    <SttFormSearch
                        caption={t('Search')}
                        value={searchingkeyword}
                        defVal={searchingkeyword}
                        onChange={({ value }: any) =>
                            setSearchingKeyword(value as string)
                        }
                        onClickSearch={handleSearch as any}
                    />
                </Box>
                <SttButtonNew onClick={() => setChargingPurseMship(mship)} />
            </Box>

            <SttTable
                data={movs}
                columns={[
                    {
                        field: 'id',
                        title: '#',
                        type: 'id',
                    },
                    {
                        field: 'concept',
                        title: t('Concept'),
                        type: 'link',
                        value: (r: MovementData) => r.concept,
                        onClick: (r: MovementData) => openPayment(r.payment),
                        align: 'left',
                    },
                    {
                        field: 'created',
                        title: t('Created'),
                        width: 140,
                        value: (r: MovementData) => (
                            <>
                                <SttChipUser user={r.creator as any} />
                                <Box mt={1}>
                                    {new DateBuilder(
                                        r.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang?.key,
                                    ).dmyhm()}
                                </Box>
                            </>
                        ),
                    },
                    {
                        field: 'type',
                        title: t('Type'),
                        value: (r: MovementData) => (
                            <SttChipPurseMov type={r.type as any} />
                        ),
                        align: 'center',
                    },
                    {
                        field: 'pf',
                        title: t('PaymentForm'),
                        value: (r: MovementData) => r.payment.paymentForm,
                        type: 'pf',
                    },
                    { field: 'amount', title: t('Amount'), type: 'price' },
                    {
                        field: 'balance',
                        title: t('Balance'),
                        type: 'price',
                    },
                ]}
                onFetch={loadMovs}
            />

            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onClose={() => setIdPayModal(null)}
                />
            )}

            {chargingPurseMship && (
                <ChargePurseDialog
                    mship={chargingPurseMship as any}
                    onClose={() => setChargingPurseMship(null)}
                    onSuccess={() => loadMovs()}
                />
            )}
        </>
    );
}
